// Issue page accordion
//
// Accessible disclosure accordion following the WAI-ARIA Authoring Practices
// accordion pattern: https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
//
// - Each header button toggles the visibility of its associated panel and
//   reflects state via `aria-expanded`.
// - Multiple panels may be open at once.
// - Keyboard support on the header buttons:
//     Enter / Space  toggle the panel (native button behavior)
//     Arrow Down      move focus to the next header (wraps)
//     Arrow Up        move focus to the previous header (wraps)
//     Home            move focus to the first header
//     End             move focus to the last header
//
// Collapsed panels are `display: none` so they occupy no space and their
// content is removed from the tab order / accessibility tree. Opening and
// closing is animated with the Web Animations API, which animates the real
// height for a smooth, reversible transition (and can pick up an in-flight
// animation if the panel is toggled mid-transition).

const SELECTORS = {
	root: '.issue-accordion',
	panel: '.accordion-panel',
	toggle: '.accordion-toggle',
	collapse: '.panel-collapse'
};

const DURATION = 300;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

const reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

const getCollapse = toggle => toggle.closest( SELECTORS.panel ).querySelector( SELECTORS.collapse );

// Embeds inside a panel are rendered with `data-src` instead of `src` (see
// IssuesContent::defer_iframes) and only start loading once their panel opens.
// A collapsed panel is `display: none`, and editor-pasted embed markup has no
// width/height attributes, so loading it while hidden gives the frame the
// 300x150 default viewport — YouTube reads that and serves a low-resolution
// player that stays blurry after the panel opens. Waiting until the panel is
// expanded lets the player size itself against the panel's real width.
const loadEmbeds = collapse => {
	collapse.querySelectorAll( 'iframe[data-src]' ).forEach( iframe => {
		iframe.src = iframe.dataset.src;
		delete iframe.dataset.src;
	} );
};

const setState = ( toggle, expanded ) => {
	toggle.setAttribute( 'aria-expanded', String( expanded ) );
	toggle.closest( SELECTORS.panel ).classList.toggle( 'is-open', expanded );
};

const animateHeight = ( collapse, from, to, onFinish ) => {
	// Cancel any in-flight animation so a mid-transition reverse stays smooth.
	if ( collapse._anim ) {
		collapse._anim.cancel();
	}

	if ( reduceMotion.matches ) {
		onFinish();
		return;
	}

	const anim = collapse.animate(
		[ { height: `${ from }px` }, { height: `${ to }px` } ],
		{ duration: DURATION, easing: EASING }
	);

	collapse._anim = anim;

	anim.onfinish = () => {
		collapse._anim = null;
		onFinish();
	};
};

const openPanel = toggle => {
	const collapse = getCollapse( toggle );

	setState( toggle, true );

	// Current rendered height (0 when closed, or the live height mid-close).
	const from = collapse.getBoundingClientRect().height;

	collapse.style.display = 'block';

	// Kick off deferred embeds now the panel is laid out at its real width, and
	// before measuring so the iframe box is included in the target height.
	loadEmbeds( collapse );

	// scrollHeight reports the full content height even while clamped.
	const to = collapse.scrollHeight;

	animateHeight( collapse, from, to, () => {
		// Settle to the natural (auto) height so the panel stays responsive.
		collapse.style.height = '';
	} );
};

const closePanel = toggle => {
	const collapse = getCollapse( toggle );

	setState( toggle, false );

	const from = collapse.getBoundingClientRect().height;

	animateHeight( collapse, from, 0, () => {
		collapse.style.display = 'none';
		collapse.style.height = '';
	} );
};

const togglePanel = toggle => {
	if ( toggle.getAttribute( 'aria-expanded' ) === 'true' ) {
		closePanel( toggle );
	} else {
		openPanel( toggle );
	}
};

const handleKeydown = ( event, toggles, index ) => {
	let nextIndex = null;

	switch ( event.key ) {
		case 'ArrowDown':
			nextIndex = ( index + 1 ) % toggles.length;
			break;
		case 'ArrowUp':
			nextIndex = ( index - 1 + toggles.length ) % toggles.length;
			break;
		case 'Home':
			nextIndex = 0;
			break;
		case 'End':
			nextIndex = toggles.length - 1;
			break;
		default:
			return;
	}

	event.preventDefault();
	toggles[ nextIndex ].focus();
};

const setup = accordion => {
	const toggles = Array.from( accordion.querySelectorAll( SELECTORS.toggle ) );

	if ( ! toggles.length ) {
		return;
	}

	toggles.forEach( ( toggle, index ) => {
		// Sync any panel authored as expanded in the markup.
		if ( toggle.getAttribute( 'aria-expanded' ) === 'true' ) {
			const collapse = getCollapse( toggle );

			setState( toggle, true );
			collapse.style.display = 'block';
			loadEmbeds( collapse );
		}

		toggle.addEventListener( 'click', () => togglePanel( toggle ) );
		toggle.addEventListener( 'keydown', event => handleKeydown( event, toggles, index ) );
	} );
};

export default () => {
	document.querySelectorAll( SELECTORS.root ).forEach( setup );
};
